import { act } from "./api.ts";

export type SignedIn = { account: string; handle: string };

function fromBase64Url(value: string): ArrayBuffer {
  const padded = value.replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(padded + "=".repeat((4 - (padded.length % 4)) % 4));

  return Uint8Array.from(raw, (character) => character.charCodeAt(0)).buffer;
}

function toBase64Url(bytes: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

export function passkeysWork(): boolean {
  return typeof window !== "undefined" && !!window.PublicKeyCredential;
}

export type Assertion = {
  credentialId: string;
  authenticatorData: string;
  clientDataJson: string;
  signature: string;
};

async function aChallengeFor(purpose: "sign-in" | "enrol"): Promise<ArrayBuffer> {
  const issued = await act<{ challenge: string }>("auth.challenge", { purpose });
  return fromBase64Url(issued.challenge);
}

export async function assertionFrom(
  mediation: CredentialMediationRequirement = "optional",
  signal?: AbortSignal,
): Promise<Assertion> {
  const challenge = await aChallengeFor("sign-in");
  const got = await navigator.credentials.get({
    mediation,
    ...(signal ? { signal } : {}),
    publicKey: {
      challenge,

      userVerification: "preferred",
    },
  });
  if (!got) throw new Error("That device did not answer.");
  const assertion = got as PublicKeyCredential;
  const said = assertion.response as AuthenticatorAssertionResponse;
  return {
    credentialId: assertion.id,
    authenticatorData: toBase64Url(said.authenticatorData),
    clientDataJson: toBase64Url(said.clientDataJSON),
    signature: toBase64Url(said.signature),
  };
}

export async function signInWithAPasskey(
  mediation: CredentialMediationRequirement = "optional",
  signal?: AbortSignal,
): Promise<SignedIn> {
  return act<SignedIn>("auth.login", await assertionFrom(mediation, signal));
}

export type Enrolment = {
  attachment?: string;
  transports?: string[];
  discoverable?: boolean;
  aaguid?: string;
};

const SAID_NOTHING = "00000000-0000-0000-0000-000000000000";

export function aaguidIn(authenticatorData: ArrayBuffer): string | undefined {
  const bytes = new Uint8Array(authenticatorData);
  if (bytes.length < 53) return undefined;
  const hex = Array.from(bytes.slice(37, 53), (byte) => byte.toString(16).padStart(2, "0")).join("");
  const dashed = `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  return dashed === SAID_NOTHING ? undefined : dashed;
}

export function enrolmentOf(made: PublicKeyCredential): Enrolment {
  const enrolled = made.response as AuthenticatorAttestationResponse;
  const enrolment: Enrolment = {};

  const attachment = made.authenticatorAttachment;
  if (attachment) enrolment.attachment = attachment;

  const transports = enrolled.getTransports?.();
  if (transports?.length) enrolment.transports = transports;

  const discoverable = made.getClientExtensionResults().credProps?.rk;
  if (typeof discoverable === "boolean") enrolment.discoverable = discoverable;

  const aaguid = enrolled.getAuthenticatorData ? aaguidIn(enrolled.getAuthenticatorData()) : undefined;
  if (aaguid) enrolment.aaguid = aaguid;

  return enrolment;
}

export type MadeDevice = {
  credentialId: string;
  publicKey: string;

  made: { clientDataJson: string; authenticatorData: string };
  attachment?: string;
  transports?: string[];
  discoverable?: boolean;
  aaguid?: string;
};

export async function makeADevice(
  user: { id: BufferSource; handle: string },
  besides: string[] = [],
): Promise<MadeDevice> {
  const challenge = await aChallengeFor("enrol");
  const made = (await navigator.credentials.create({
    publicKey: {
      challenge,

      rp: { name: "Rally" },

      user: { id: user.id, name: user.handle, displayName: user.handle },

      pubKeyCredParams: [
        { type: "public-key", alg: -7 },
        { type: "public-key", alg: -8 },
      ],

      authenticatorSelection: { residentKey: "required", userVerification: "preferred" },
      ...(besides.length
        ? {
            excludeCredentials: besides.map((id) => ({
              type: "public-key" as const,
              id: fromBase64Url(id),
            })),
          }
        : {}),

      attestation: "direct",

      extensions: { credProps: true },
    },
  })) as PublicKeyCredential | null;
  if (!made) throw new Error("That device did not answer.");

  const enrolled = made.response as AuthenticatorAttestationResponse;
  const spki = enrolled.getPublicKey();
  if (!spki) throw new Error("This browser did not give the new device's public key.");

  const authenticatorData = enrolled.getAuthenticatorData?.();
  if (!authenticatorData) {
    throw new Error("This browser will not say what it just made, so this key cannot be enrolled.");
  }

  return {
    credentialId: made.id,
    publicKey: toBase64Url(spki),
    made: {
      clientDataJson: toBase64Url(enrolled.clientDataJSON),
      authenticatorData: toBase64Url(authenticatorData),
    },
    ...enrolmentOf(made),
  };
}

export type Registering = {
  id: Uint8Array<ArrayBuffer>;
  handle: string;
  devices: MadeDevice[];
};

export function beginRegistering(handle: string): Registering {
  return { id: crypto.getRandomValues(new Uint8Array(32)), handle, devices: [] };
}

export async function addAPasskey(registering: Registering): Promise<MadeDevice> {
  const made = await makeADevice(
    { id: registering.id, handle: registering.handle },
    registering.devices.map((device) => device.credentialId),
  );
  registering.devices.push(made);
  return made;
}

export async function registerWith(
  invite: string,
  registering: Registering,

  called: (string | undefined)[] = [],
): Promise<SignedIn> {
  return act<SignedIn>("auth.register", {
    invite,
    handle: registering.handle,
    devices: registering.devices.map((device, at) => ({ ...device, called: called[at] ?? "" })),
  });
}

export async function addADevice(
  me: { account: string; handle: string },
  proving: CredentialMediationRequirement = "optional",
): Promise<{ devices: number }> {
  const device = await makeADevice({
    id: new TextEncoder().encode(me.account),
    handle: me.handle,
  });
  const proof = await assertionFrom(proving);

  return act<{ devices: number }>("passkey.add", { ...device, proof });
}

export async function removeADevice(credentialId: string): Promise<{ devices: number }> {
  return act<{ devices: number }>("passkey.remove", { credentialId });
}
